import { bareApi } from 'constants/api';
import { ApiResponse } from 'types/utils/api-response';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

type TokenNames = 'access_token' | 'refresh_token';

export type AuthTokensObject = Record<TokenNames, string>;

export interface AuthStore {
  tokens: Record<TokenNames, AuthTokensObject[TokenNames] | null>;
  isLoggedIn: boolean;
  actions: {
    setTokens: (tokens: AuthTokensObject) => void;
    setLoggedIn: (logged: boolean) => void;
    refreshSession: () => Promise<void>;
    logout: () => void;
  };
}

export const AUTH_STORE_NAME = 'auth-store';

const initialAuthStore: Omit<AuthStore, 'actions'> = {
  tokens: {
    access_token: null,
    refresh_token: null,
  },
  isLoggedIn: false,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialAuthStore,
        actions: {
          setTokens: (tokens) => {
            set({
              tokens,
            });
          },
          setLoggedIn: (logged) => {
            set({
              isLoggedIn: logged,
            });
          },
          logout: () => {
            set({ ...initialAuthStore });
          },
          refreshSession: async () => {
            const { refresh_token } = get().tokens;
            const { data } = await bareApi.post<
              ApiResponse<Pick<AuthTokensObject, 'access_token'>>
            >(
              '/auth/refresh',
              { refresh_token },
              {
                headers: {
                  // from unknown reasons this headers is not set here
                  'Content-Type': 'application/json',
                },
              }
            );

            set((state) => ({
              tokens: {
                ...state.tokens,
                access_token: data.data.access_token,
              },
            }));
          },
        },
      }),
      {
        name: AUTH_STORE_NAME,
        partialize: (state) => ({
          isLoggedIn: state.isLoggedIn,
          tokens: state.tokens,
        }),
      }
    ),
    {
      name: AUTH_STORE_NAME,
    }
  )
);

export const useAuthStoreActions = () => useAuthStore((state) => state.actions);
